export const CHECKOUT_URL = "https://hof-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/9TrMprPM1pYn75tJxmLtkL/MOCKUP_HOF-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/iKwdVbTEtm6Br2RANBTqb2/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const collectionItems = [
  "6 specialized volumes",
  "5 professional bonuses",
  "Clinical assessment frameworks",
  "Patient documentation templates",
  "Educational case studies",
  "Safety and follow-up checklists",
];

export const beforeItems = [
  "You search through different resources to find information you have studied before.",
  "You struggle to organize all the factors involved in evaluating a complex aesthetic case.",
  "You have difficulty keeping patient documentation consistent.",
  "You want a clearer way to review potential complications and warning signs.",
  "You need a practical resource to support your continuing professional education.",
];

export const afterItems = [
  "You have a structured educational library organized by clinical topic.",
  "You can use assessment frameworks to organize the information relevant to a case.",
  "You have templates to support consistent patient documentation.",
  "You can review educational materials about potential complications and safety considerations.",
  "You can use checklists to prepare for appointments and organize follow-up.",
];

export const painPoints = [
  {
    title: "You Have Too Many Resources and No Central Reference Library",
    text: "Your knowledge is spread across courses, saved files, textbooks, and personal notes. Finding the relevant material takes longer than it should.",
  },
  {
    title: "You Want a More Structured Way to Review Complex Cases",
    text: "You know that a careful assessment involves more than looking at the treatment area, but you want a clearer framework for organizing the information.",
  },
  {
    title: "You Want to Improve Your Clinical Documentation",
    text: "You need a more consistent way to record patient history, treatment details, follow-up information, and unexpected events.",
  },
  {
    title: "You Want to Recognize Potential Warning Signs More Confidently",
    text: "You want to improve your understanding of reactions that may require additional evaluation, urgent assessment, or specialist referral.",
  },
  {
    title: "You Need Practical Materials for Your Daily Routine",
    text: "You do not want another collection of disconnected theoretical explanations. You want educational references, checklists, and templates that you can actually use.",
  },
  {
    title: "You Want to Keep Your Clinical Knowledge Organized",
    text: "You want a resource that makes it easier to revisit important concepts instead of repeatedly searching for the same information.",
  },
];

export const volumes = [
  {
    numeral: "I",
    accent: "#E7B84A",
    title: "The Facial Aesthetic Clinical Safety Framework",
    subtitle: "Build a structured foundation for safer clinical assessment.",
    text: "In this volume, I bring together the core principles that support a systematic approach to facial aesthetic practice. You will explore patient history, preliminary assessment, treatment considerations, contraindication screening, documentation, and the importance of recognizing when a case requires additional evaluation.",
    listLabel: "Inside, you will find",
    items: [
      "Patient assessment frameworks",
      "Pre-procedure safety checklists",
      "Clinical documentation considerations",
      "Risk-awareness worksheets",
      "Case preparation templates",
    ],
    note: "The objective is to help you organize the information you need to review before making clinical decisions.",
  },
  {
    numeral: "II",
    accent: "#3B82F6",
    title: "Facial Anatomy and High-Risk Area Assessment",
    subtitle: "Understand the anatomical considerations that deserve special attention in facial aesthetic procedures.",
    text: "Facial anatomy is fundamental to understanding the risks associated with injectable treatments. In this volume, I focus on educational anatomy references and the clinical significance of anatomical variation.",
    listLabel: "You will explore",
    items: [
      "Facial vascular anatomy",
      "Anatomical relationships relevant to injectable procedures",
      "Areas requiring particular clinical attention",
      "Anatomical variation and individual assessment",
      "Visual reference sheets for professional study",
    ],
    note: "The material is intended to support anatomical education, not to prescribe universally safe injection depths, locations, or techniques.",
  },
  {
    numeral: "III",
    accent: "#EC6F8E",
    title: "Complication Recognition and Clinical Assessment",
    subtitle: "Organize your understanding of unexpected reactions and potential complications.",
    text: "Not every post-treatment reaction has the same significance. Recognizing the difference between expected effects and potential warning signs is an important part of professional assessment.",
    listLabel: "Educational material covering",
    items: [
      "Common post-treatment reactions",
      "Swelling, nodules, and inflammatory presentations",
      "Possible signs of infection",
      "Warning signs associated with potential vascular compromise",
      "Clinical findings that require urgent evaluation",
      "Documentation and referral considerations",
    ],
    note: "You will also find comparative tables and educational case scenarios to help you review the concepts systematically.",
  },
  {
    numeral: "IV",
    accent: "#E5484D",
    title: "Vascular Complications: Recognition and Emergency Preparedness",
    subtitle: "Study the warning signs that require immediate attention.",
    text: "Potential vascular complications associated with injectable fillers demand particular attention because delayed recognition can have serious consequences. I designed this volume to help qualified practitioners study the clinical significance of warning signs and understand the importance of timely escalation.",
    listLabel: "Topics include",
    items: [
      "Recognizing concerning changes in skin appearance",
      "Understanding the significance of unusual or escalating pain",
      "Identifying symptoms that may indicate ocular involvement",
      "Documenting the onset and progression of symptoms",
      "Understanding the importance of urgent specialist assessment",
      "Reviewing emergency preparedness and referral pathways",
    ],
    note: "This volume is an educational reference. It does not replace hands-on training, current emergency protocols, or individualized management by qualified professionals.",
  },
  {
    numeral: "V",
    accent: "#2FBF71",
    title: "Injectable Treatment Complications: Clinical Reference",
    subtitle: "Build a more organized understanding of complications associated with hyaluronic acid fillers and botulinum toxin.",
    text: "Different products and procedures can be associated with different adverse effects. Understanding those distinctions helps practitioners organize their assessments and identify when additional evaluation is needed.",
    listLabel: "Educational reviews of",
    items: [
      "Filler-associated swelling and nodules",
      "Possible inflammatory and infectious complications",
      "Unwanted aesthetic outcomes",
      "Eyelid ptosis and asymmetry following botulinum toxin treatment",
      "Other adverse effects requiring clinical evaluation",
      "Follow-up documentation and referral considerations",
    ],
    note: "Comparative tables help you organize the information by presentation, potential significance, and appropriate need for further evaluation.",
  },
  {
    numeral: "VI",
    accent: "#8B5CF6",
    title: "The Clinical Documentation and Follow-Up System",
    subtitle: "Organize your patient records and follow-up process with greater consistency.",
    text: "A structured clinical record helps practitioners document relevant information, communicate clearly, and maintain a consistent approach to patient follow-up. In this final volume, I focus on the practical systems that support the broader clinical workflow.",
    listLabel: "You will find",
    items: [
      "Patient history and assessment templates",
      "Treatment documentation worksheets",
      "Photographic documentation checklists",
      "Follow-up record templates",
      "Adverse-event documentation forms",
      "Patient communication and referral records",
      "Clinical review checklists",
    ],
    note: "These resources are designed to be adapted to your professional setting, applicable regulations, and existing clinical procedures.",
  },
];

export const resources = [
  {
    title: "Clinical Assessment Frameworks",
    text: "Structured worksheets to help organize relevant patient information and assessment considerations.",
  },
  {
    title: "Patient Documentation Templates",
    text: "Forms to support consistent recording of patient history, treatment details, and follow-up information.",
  },
  {
    title: "Safety Review Checklists",
    text: "Practical checklists for reviewing preparation, documentation, and relevant safety considerations.",
  },
  {
    title: "Educational Case Studies",
    text: "Hypothetical clinical scenarios for reviewing concepts, recognizing potential warning signs, and discussing clinical reasoning.",
  },
  {
    title: "Comparative Reference Tables",
    text: "Organized educational summaries to help you review relevant concepts without searching through multiple documents.",
  },
];

export const bonuses = [
  {
    number: 1,
    image: "https://cdn.magicpatterns.com/uploads/gcdkbA6LfjFLqXHcuNDPHf/BONUS_1_HOF-EN.png",
    title: "Facial Aesthetic Clinical Casebook",
    subtitle: "A collection of educational cases for structured clinical review.",
    text: "Explore hypothetical patient scenarios involving different facial aesthetic presentations. Each case is organized around the available clinical information, relevant assessment considerations, potential warning signs, and questions for professional discussion. You will learn how to approach each case as a structured learning exercise rather than relying on isolated facts.",
    items: [
      "Case presentation sheets",
      "Patient history summaries",
      "Clinical observation worksheets",
      "Discussion questions",
      "Educational review notes",
    ],
    note: "",
  },
  {
    number: 2,
    image: "https://cdn.magicpatterns.com/uploads/rFj21K9W5F2fRAEABaRA57/BONUS_2_HOF-EN.png",
    title: "The Facial Aesthetic Pre-Procedure Assessment Toolkit",
    subtitle: "A structured toolkit for reviewing patients before treatment.",
    text: "Use these educational templates to organize the information that should be reviewed before an injectable procedure. The toolkit includes patient history worksheets, contraindication screening references, documentation checklists, and preparation forms.",
    items: [
      "Patient assessment worksheet",
      "Medical history review form",
      "Pre-procedure documentation checklist",
      "Treatment planning considerations",
      "Patient information review sheet",
    ],
    note: "The forms should be adapted to the practitioner’s scope of practice and applicable professional requirements.",
  },
  {
    number: 3,
    image: "https://cdn.magicpatterns.com/uploads/3Pb7XuvEjprvEiL1RSz8Hu/BONUS_3_HOF-EN.png",
    title: "The Facial Aesthetic Warning Signs Atlas",
    subtitle: "A visual educational reference to help you study potentially concerning clinical presentations.",
    text: "This resource organizes important warning signs into an easy-to-review format. It covers changes in skin appearance, unusual pain, progressive swelling, possible infection, and symptoms that may indicate ocular involvement.",
    items: [
      "Visual learning tables",
      "Warning-sign reference cards",
      "Clinical observation worksheets",
      "Symptom documentation templates",
      "Urgency and escalation considerations",
    ],
    note: "The atlas is intended for professional education and does not replace emergency assessment or current clinical protocols.",
  },
  {
    number: 4,
    image: "https://cdn.magicpatterns.com/uploads/3YETzoRZqp8Ma9C2zRSazW/BONUS_4_HOF-EN.png",
    title: "Patient Follow-Up and Clinical Documentation Toolkit",
    subtitle: "A collection of templates to organize communication and follow-up.",
    text: "Create a more consistent approach to documenting patient progress and recording relevant communications after aesthetic procedures.",
    items: [
      "Follow-up record templates",
      "Patient communication examples",
      "Treatment review forms",
      "Adverse-event documentation worksheets",
      "Referral and escalation record templates",
    ],
    note: "All patient-facing materials should be reviewed and adapted to your local professional requirements.",
  },
  {
    number: 5,
    image: "https://cdn.magicpatterns.com/uploads/iB7sLjhVMb45WxqV6DHqGa/BONUS_5_HOF-EN.png",
    title: "Facial Aesthetic Clinical Safety Audit Workbook",
    subtitle: "A practical workbook for reviewing your clinical safety systems.",
    text: "Use this workbook to examine the organization of your clinical environment, documentation practices, emergency preparedness, and follow-up procedures.",
    items: [
      "Practice readiness checklist",
      "Equipment and resource review sheets",
      "Documentation quality checklist",
      "Emergency preparedness review",
      "Follow-up process audit",
      "Improvement planning worksheets",
    ],
    note: "The workbook helps you identify areas for review; it does not certify compliance or replace applicable professional standards.",
  },
];

export const testimonials = [
  {
    name: "Sofia R., RN, BSN",
    role: "Aesthetic Nurse Injector · United States",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/4e037383-068a-4cc1-90f0-c5ecd90f0f96.jpg",
    quote:
      "I finally have one place where my pre-procedure checklists and documentation templates live. I review the relevant volume before complex appointments and my preparation feels far more consistent.",
  },
  {
    name: "Dr. Daniel M., DDS",
    role: "Dentist, Facial Aesthetics · Canada",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/1099f1c5-882d-446c-ac8e-8e0e175651bf.jpg",
    quote:
      "The anatomy reference sheets and the casebook became part of my weekly study routine. Working through the discussion questions is a much better use of my time than digging through old course notes.",
  },
  {
    name: "Dr. Claire H.",
    role: "Dermatologist · United Kingdom",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/fa2e184c-38bd-4104-b976-1f969ed7734d.jpg",
    quote:
      "The comparative tables in Volumes III and V are exactly the kind of organized summaries I used to build by hand. Having them side by side makes reviewing presentations much quicker.",
  },
  {
    name: "Dr. Amara O.",
    role: "Aesthetic Physician · United States",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/2be9fbc0-2697-4320-98e2-24ecf817c462.jpg",
    quote:
      "I used the Safety Audit Workbook to review my clinic's emergency preparedness and follow-up process with my team. It gave us a clear structure for a conversation we had been postponing.",
  },
];

export const guaranteeItems = [
  "Explore the six volumes",
  "Review the clinical reference materials",
  "Examine the educational case studies",
  "Browse the documentation templates",
  "Evaluate the five specialized bonuses",
];

export const faqs = [
  {
    q: "How will I receive the materials?",
    a: "After your payment is confirmed, you will receive instructions to access your digital collection. The volumes and bonuses will be provided in the formats specified on the checkout page.",
  },
  {
    q: "What exactly is included?",
    a: "Your purchase includes six educational volumes and five specialized resources covering facial aesthetic clinical safety, anatomy, complication recognition, assessment, documentation, and follow-up.",
  },
  {
    q: "Is this a physical product?",
    a: "No. HOF CLINICAL BLUEPRINT™ is a digital educational collection. No physical book will be shipped.",
  },
  {
    q: "Who is this collection for?",
    a: "It is intended for appropriately qualified facial aesthetic practitioners and other eligible professionals seeking supplementary educational references relevant to their scope of practice.",
  },
  {
    q: "Is this a complete training course?",
    a: "No. It is an educational reference collection, not a substitute for accredited training, supervised clinical education, or professional qualification.",
  },
  {
    q: "Does this replace clinical judgment or emergency protocols?",
    a: "No. The materials are intended to support education and reference. Clinical decisions must be made by appropriately qualified professionals in accordance with current evidence, individual patient circumstances, applicable regulations, and relevant emergency protocols.",
  },
  {
    q: "Can I use the materials in any country?",
    a: "The educational concepts may be relevant across different settings, but professional scopes of practice, regulations, and clinical guidelines vary. You are responsible for ensuring that any use of the materials is consistent with local requirements.",
  },
  {
    q: "Can I download the files?",
    a: "The collection is delivered digitally. Download availability and file formats will be specified on the access page.",
  },
  {
    q: "Is this a recurring subscription?",
    a: "No. The advertised price is a one-time payment of $27 USD, with no recurring subscription for this collection.",
  },
  {
    q: "What if I decide the collection is not right for me?",
    a: "The purchase includes a seven-day refund guarantee, subject to the terms provided at checkout. Contact support within the guarantee period for instructions.",
  },
  {
    q: "Does the collection guarantee better clinical outcomes?",
    a: "No. The collection provides educational information and organizational resources. It does not guarantee clinical outcomes or eliminate the risks associated with aesthetic procedures.",
  },
];

export const recentBuyers = [
  { name: "María G.", country: "Mexico" },
  { name: "Lucía F.", country: "Argentina" },
  { name: "Carlos R.", country: "Colombia" },
  { name: "Valentina R.", country: "Chile" },
  { name: "Andrés P.", country: "Peru" },
  { name: "Camila T.", country: "Spain" },
  { name: "Sofía H.", country: "Uruguay" },
  { name: "Diego C.", country: "Ecuador" },
  { name: "Isabella M.", country: "Costa Rica" },
  { name: "Mateo V.", country: "Bolivia" },
  { name: "Daniela O.", country: "Paraguay" },
  { name: "José M.", country: "Venezuela" },
  { name: "Gabriela N.", country: "Dominican Republic" },
  { name: "Fernanda S.", country: "Guatemala" },
  { name: "Ricardo S.", country: "Panama" },
];
